import { AuthedPage, HeaderFooterLayout } from '../app';
import { useParams } from 'wouter';
import { ProjectHub } from '@platform/client';
import { BackToMyProjectsButton } from './components/BackToMyProjectsButton';
import { ProjectDetailsSection } from './components/ProjectDetailsSection';
import { UpdateProjectDatesModalProvider } from '../dashboard/my-projects/Modals/UpdateProjectDatesModal';
import { ProgressAccordion } from './progress-accordion';
import { ProjectPlanCard, SideBar, SideBarItemProps } from './components/SideBar';
import ErrorPage from '../../ErrorPage';
import Skeleton from 'react-loading-skeleton';
import { SimplifiedUserRole } from '@platform/client/schemas';
import { ROUTES } from '../routes/consts';

export const HubPage = () => {
  const { matchGroupId } = useParams<{ matchGroupId: string }>();
  const {
    data: hubData,
    isSuccess,
    isLoading,
    isError,
  } = ProjectHub.useGetProjectHubDataByMatchGroup(parseInt(matchGroupId));
  const projectPlanner = hubData?.displayPlanUi ?? true;
  const isProfessional = hubData?.userType === SimplifiedUserRole.PROFESSIONAL;
  const professionalItems: SideBarItemProps[] =
    hubData?.professionals.map((professional) => ({
      type: 'contact',
      firstname: professional.firstname ?? '',
      lastname: professional.lastname ?? '',
      email: professional.email ?? '',
      avatarUrl: professional.avatar ?? undefined,
      profileUrl: `${ROUTES.DIRECTORY}/professionals/${professional.id}`,
    })) ?? [];
  const representativeItems: SideBarItemProps[] = [
    {
      type: 'contact',
      firstname: hubData?.representative?.firstname ?? '',
      lastname: hubData?.representative?.lastname ?? '',
      email: hubData?.representative?.email ?? '',
      avatarUrl: hubData?.representative?.avatar ?? undefined,
      profileUrl: undefined,
    },
  ];
  // Team: show "Your Team" (all professionals). Professionals also see their representative.
  // Representatives on a non-team project see the single professional instead.
  const sideBars: { title: string; items: SideBarItemProps[] }[] = [
    ...(isProfessional ? [{ title: 'Representative', items: representativeItems }] : []),
    ...(hubData?.teamId ? [{ title: 'Your Team', items: professionalItems }]
    : !isProfessional ? [{ title: 'Professional', items: professionalItems }]
    : []),
  ];
  return (
    <AuthedPage>
      <HeaderFooterLayout useDashboardHeader withSidebar={false}>
        <UpdateProjectDatesModalProvider>
          {isError && <ErrorPage />}
          <div className='w-full md:max-w-[1280px] mx-auto px-4 md:px-20 mb-10 -mt-4 text-gray-700'>
            <div className='flex flex-col gap-4 md:gap-10 pb-8 border-b border-gray-100'>
              <BackToMyProjectsButton />
              {isLoading && <Skeleton height={200} />}
              {isSuccess && (
                <ProjectDetailsSection
                  organization={hubData?.organization}
                  status={hubData?.status}
                  project={hubData?.project}
                />
              )}
            </div>
            <div className='mx-auto mt-8 md:flex gap-8'>
              <div className='flex-grow border border-gray-100 rounded-xl h-fit overflow-auto'>
                {isLoading && <Skeleton height={400} />}
                {isSuccess && <ProgressAccordion hubData={hubData} />}
              </div>
              <div className='flex flex-col mt-8 md:mt-0 gap-8'>
                {isLoading && <Skeleton height={400} />}
                {isSuccess && (
                  <div className='flex flex-col gap-8'>
                    {projectPlanner && <ProjectPlanCard projectPlanId={hubData.projectPlanId} />}
                    {sideBars.map(({ title, items }) => (
                      <SideBar key={title} title={title} items={items} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </UpdateProjectDatesModalProvider>
      </HeaderFooterLayout>
    </AuthedPage>
  );
};
